import axios from "axios";
import { getValidToken } from "./authService";
import { getCategories } from "./categoryService";
import {
  isDemoMode,
  demoGetTotals,
  demoGetAllCategoryStats,
  demoGetExpensesByCategory,
  demoGetIncomeByCategory,
  demoGetTopExpenses,
  demoGetCategoryStats,
  demoGetCategoriesWithGroups,
  demoGetAllCategoriesWithGroups,
  demoGetExpensesByVendor,
  demoGetInvestmentsBySource,
  demoGetUpcomingRecurring,
} from "../demo/demoStore";

const API_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: API_URL,
});

apiClient.interceptors.request.use(
  async (config) => {
    const token = await getValidToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const addContextParams = (params, contextParams) => {
  if (contextParams.context) {
    params.context = contextParams.context;
  }
  if (contextParams.groupId) {
    params.groupId = contextParams.groupId;
  }
  // Add categoryIds to params if they exist and are not empty
  if (contextParams.categoryIds && contextParams.categoryIds.length > 0) {
    params.categoryIds = JSON.stringify(contextParams.categoryIds);
  }
  return params;
};

const calculateDateRangeFromMonths = (months) => {
  const today = new Date();

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  if (months >= 24) {
    const yearsToGoBack = Math.floor(months / 12);
    const currentYear = today.getFullYear();

    const startYear = currentYear - yearsToGoBack + 1;
    const startOfPeriod = new Date(startYear, 0, 1); // January 1st

    return {
      startDate: formatDate(startOfPeriod),
      endDate: formatDate(today),
    };
  }

  const startOfPeriod = new Date(
    today.getFullYear(),
    today.getMonth() - months + 1,
    1,
  );

  // Always use today's date as the end date - this ensures it's always current
  return {
    startDate: formatDate(startOfPeriod),
    endDate: formatDate(today),
  };
};

export const getTotals = async (dateRange, contextParams) => {
  if (isDemoMode()) return demoGetTotals();
  const params = addContextParams({}, contextParams);
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) throw new Error("Invalid date range provided");
  params.start_date = dateRange.startDate;
  params.end_date = dateRange.endDate;
  const response = await apiClient.get("/stats", { params });
  return response.data;
};

export const getAllCategoryStats = async (dateRange, contextParams) => {
  if (isDemoMode()) return demoGetAllCategoryStats();
  const params = addContextParams({}, contextParams);
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) throw new Error("Invalid date range provided");
  params.start_date = dateRange.startDate;
  params.end_date = dateRange.endDate;
  const response = await apiClient.get("/stats/categories", { params });
  return response.data;
};

export const getCategoryStats = async (dateRange, category, contextParams) => {
  if (isDemoMode()) return demoGetCategoryStats(dateRange, category);
  const params = addContextParams({ category }, contextParams);
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) throw new Error("Invalid date range provided");
  params.start_date = dateRange.startDate;
  params.end_date = dateRange.endDate;
  const response = await apiClient.get("/stats/category", { params });
  return response.data;
};

export const getExpensesByCategory = async (dateRange, contextParams) => {
  if (isDemoMode()) return demoGetExpensesByCategory();
  const params = addContextParams({}, contextParams);
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) throw new Error("Invalid date range provided");
  params.start_date = dateRange.startDate;
  params.end_date = dateRange.endDate;
  const response = await apiClient.get("/stats/expenses-by-category", { params });
  return response.data;
};

export const getInvestmentsBySource = async (dateRange, contextParams) => {
  if (isDemoMode()) return demoGetInvestmentsBySource();
  const params = addContextParams({}, contextParams);
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) throw new Error("Invalid date range provided");
  params.start_date = dateRange.startDate;
  params.end_date = dateRange.endDate;
  const response = await apiClient.get("/stats/investments-by-source", { params });
  return response.data;
};

export const getIncomeByCategory = async (dateRange, contextParams) => {
  if (isDemoMode()) return demoGetIncomeByCategory();
  const params = addContextParams({}, contextParams);
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) throw new Error("Invalid date range provided");
  params.start_date = dateRange.startDate;
  params.end_date = dateRange.endDate;
  const response = await apiClient.get("/stats/income-by-category", { params });
  return response.data;
};

export const getExpensesByVendor = async (dateRange, contextParams) => {
  if (isDemoMode()) return demoGetExpensesByVendor();
  const params = addContextParams({}, contextParams);
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) throw new Error("Invalid date range provided");
  params.start_date = dateRange.startDate;
  params.end_date = dateRange.endDate;
  const response = await apiClient.get("/stats/expenses-by-vendor", { params });
  return response.data;
};

export const getCategoriesWithGroups = async (dateRange, contextParams) => {
  if (isDemoMode()) return demoGetCategoriesWithGroups();
  const params = addContextParams({}, contextParams);
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) throw new Error("Invalid date range provided");
  params.start_date = dateRange.startDate;
  params.end_date = dateRange.endDate;
  const response = await apiClient.get(`/stats/categories-with-groups`, { params });
  return response.data;
};

export const getAllCategoriesWithGroups = async (contextParams) => {
  if (isDemoMode()) return demoGetAllCategoriesWithGroups();
  if (contextParams.context !== "personal") {
    return getCategories(contextParams);
  }
  const params = addContextParams({}, contextParams);
  const response = await apiClient.get(`/stats/categories-all`, { params });
  return response.data;
};

export const getUpcomingRecurringPayments = async (months = 1, contextParams) => {
  if (isDemoMode()) return demoGetUpcomingRecurring();
  const params = addContextParams({ months }, contextParams);
  const response = await apiClient.get("/stats/upcoming-recurring", { params });
  return response.data;
};

export const getTopExpenses = async (dateRange, contextParams) => {
  if (isDemoMode()) return demoGetTopExpenses();
  const params = addContextParams({}, contextParams);
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) throw new Error("Invalid date range provided");
  params.start_date = dateRange.startDate;
  params.end_date = dateRange.endDate;
  const response = await apiClient.get("/stats/top-expenses", { params });
  return response.data;
};

export const getDateRangeFromMonths = calculateDateRangeFromMonths;
